import Add16 from '@carbon/icons-react/es/add/16';
import { List } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, RefObject, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TaskId } from 'api/models';
import { FieldOption, StatusGroup } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Empty } from 'components/atoms/empty';
import { NewRow } from 'components/molecules/new-row';
import { HelperText01, Text03 } from 'components/styles';
import { PermissionRuleName } from 'constant';
import { Task } from 'data/tasks';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslation } from 'hooks/use-translation';
import { TaskItem } from './task-item';
import styles from './tasks-list.module.css';

export const EmptyMessage = styled.span`
  ${HelperText01};
  ${Text03};
`;
interface TaskListProps {
  assignees?;
  canAddDate?: boolean;
  className?: string;
  disableStatusDropdown?: boolean;
  size?: SizeType;
  disableNewRow?: boolean;
  data: Array<Task>;
  readOnly?: boolean;
  refTo: RefObject<any>;
  renderExtraActions?: (task) => React.ReactNode;
  handleAssigneeChange: (taskId: number, selectedAssignee, options?) => void;
  handleStatusChange: (
    taskId: number,
    selectedStatus: FieldOption<StatusGroup>,
  ) => void;
  handleDueDateChange: (
    taskId: number,
    payload: {
      startDate?: number;
      dueDate?: number;
    },
  ) => void;
  handleDelete: (task: Task) => void;
  handleNewTaskAdded: (taskName: string) => Promise<void>;
  handleMultipleTasksAdded?: (tasks: Array<string>) => Promise<void>;
  onItemClick: (item: TaskId) => void;
  onEnableTaskAddRow?(): void;
}

export const TaskList: FC<TaskListProps> = (props) => {
  const {
    canAddDate,
    className,
    disableStatusDropdown,
    size,
    disableNewRow,
    data = [],
    readOnly,
    refTo,
    renderExtraActions,
    handleAssigneeChange,
    handleStatusChange,
    handleDueDateChange,
    handleDelete,
    handleNewTaskAdded,
    handleMultipleTasksAdded,
    onItemClick,
    onEnableTaskAddRow,
  } = props;
  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!data.length);
  const translatedPlaceholderText = useTranslation('placeholder.task.new');

  useEffect(() => {
    setCanShowEmpty(!data.length);
  }, [data.length]);

  const _onEnableTaskAddRow = () => {
    setCanShowEmpty(false);

    if (onEnableTaskAddRow) {
      onEnableTaskAddRow();
    }
  };
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasTaskCreateAccess = isAllowedForCurrentUser(
    PermissionRuleName.TaskCreateAction,
  );

  return !canShowEmpty ? (
    <div className={className} ref={refTo}>
      {data.length > 0 && (
        <div className={styles.wrapper}>
          <List
            dataSource={data}
            renderItem={(record: Task) => (
              <List.Item key={record.taskId}>
                <TaskItem
                  canAddDate={canAddDate}
                  disableStatusDropdown={disableStatusDropdown}
                  size={size}
                  attachTo={refTo.current}
                  readOnly={readOnly}
                  renderExtraActions={renderExtraActions}
                  data={record}
                  onTaskNameClick={onItemClick}
                  handleAssigneeChange={handleAssigneeChange}
                  handleStatusChange={handleStatusChange}
                  handleDueDateChange={handleDueDateChange}
                  onDelete={handleDelete}
                />
              </List.Item>
            )}
          />
        </div>
      )}
      {!disableNewRow && !readOnly && hasTaskCreateAccess ? (
        <NewRow
          onAdd={handleNewTaskAdded}
          onAddMultiple={handleMultipleTasksAdded}
          placeholder={translatedPlaceholderText}
          size={size}
          bulkAddType="tasks"
        />
      ) : null}
    </div>
  ) : (
    <Empty>
      {!readOnly ? (
        <>
          {hasTaskCreateAccess ? (
            <Button
              ref={refTo}
              onClick={_onEnableTaskAddRow}
              className={styles.add_btn}
              type="link"
              size="small"
              icon={<Add16 />}
              data-cy="spaces-tasks.tab-add-task-btn"
            >
              <FormattedMessage id="task.add" />
            </Button>
          ) : (
            <EmptyMessage>
              <FormattedMessage id="reports.template_insight.table.empty.task" />
            </EmptyMessage>
          )}
        </>
      ) : null}
    </Empty>
  );
};
